<script setup lang="ts">
import { ref } from 'vue'

import { useMessagesStore, type SnackbarMessage } from '@/stores/messagesStore'
import mdiClose from '~icons/mdi/close'

const messages = useMessagesStore()

/**
 * Following a message's link dismisses the queue.
 *
 * `clear()` is what VSnackbarQueue exposes; per-item dismissal is only reachable through the
 * actions slot, which the text slot cannot see. Clearing everything is coarser than closing just
 * the one clicked, but a link navigates away from the page the messages belong to, so leaving the
 * rest hanging over the new view would be worse.
 */
const queue = ref<{ clear: () => void } | null>(null)

/** Narrow a queue item to the object form, so the template can read the optional extras. */
function details(item: SnackbarMessage) {
  return typeof item === 'string' ? { text: item, icon: undefined, to: undefined } : item
}
</script>

<template>
  <v-snackbar-queue
    ref="queue"
    v-model="messages.queue"
    location="bottom center"
    :timeout="5000"
    :total-visible="5"
    collapsed
    closable
  >
    <!-- A message may carry a leading icon and link somewhere (ADA-4345). Plain strings and
         text-only messages render exactly as they did before. -->
    <template #text="{ item }">
      <component
        :is="details(item).to ? 'router-link' : 'div'"
        :to="details(item).to"
        class="d-flex align-center ga-3 text-decoration-none"
        :style="{ color: 'inherit' }"
        @click="details(item).to ? queue?.clear() : undefined"
      >
        <v-icon
          v-if="details(item).icon"
          :icon="details(item).icon"
        />
        <span :class="{ 'text-decoration-underline': details(item).to }">{{ details(item).text }}</span>
      </component>
    </template>

    <template #actions="{ item, props: actionProps }">
      <v-btn
        v-if="typeof item === 'string' || item.timeout !== -1"
        v-bind="actionProps"
        icon
      >
        <v-icon :icon="mdiClose" />
        <v-tooltip
          :text="$t('common.close')"
          location="top"
          activator="parent"
        />
      </v-btn>
    </template>
  </v-snackbar-queue>
</template>
